import { useLocalSearchParams } from 'expo-router'
import { useCallback, useMemo, useState } from 'react'
import { SectionList, StyleSheet, View } from 'react-native'
import { Divider, Icon, Text, useTheme } from 'react-native-paper'
import { DEFAULT_PERIOD_TYPE, DEFAULT_WEEK_START_DAY } from '@/constants/periods'
import { useCustomer } from '@/hooks/customers'
import { useApprovedJobEntries } from '@/hooks/useApprovedJobEntries'
import { useCustomerJobs } from '@/hooks/useCustomerJobs'
import { useCustomerJobsSummary } from '@/hooks/useCustomerJobsSummary'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { useOrgSettings } from '@/hooks/useOrgSettings'
import { usePeriodNavigation } from '@/hooks/usePeriodNavigation'
import { isNonBlankAddress } from '@/lib/address'
import { formatDateShort, formatDurationHHMM, getNetWorkSeconds } from '@/lib/dates'
import { displayLocationName } from '@/lib/location-utils'
import { getPeriodBounds, getPeriodInfo } from '@/lib/periods'
import type { CustomerFormData } from '@/types/customers'
import type { PeriodConfig, WeekStartDay } from '@/types/periods'
import { AddressPanel } from '@/ui/AddressPanel'
import { Card } from '@/ui/Card'
import { CollapsibleSection } from '@/ui/CollapsibleSection'
import { EmptyCard } from '@/ui/EmptyCard'
import { ListWrapper } from '@/ui/ListWrapper'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { NotFound } from '@/ui/NotFound'
import { PeriodNavigator } from '@/ui/PeriodNavigator'
import { type JobSection, JobSectionHeader } from '@/ui/time-entry/JobSectionHeader'

type Section = JobSection & { data: Array<{ type: 'entries'; jobId: string }> }

const getEntryTypeIcon = (entryType: string) => {
    switch (entryType) {
        case 'manual':
            return 'pencil'
        case 'timer':
            return 'timer-outline'
        case 'geofence':
            return 'map-marker-radius'
        case 'travel':
            return 'car'
        default:
            return 'circle-outline'
    }
}

interface JobEntriesProps {
    jobId: string
    startDate: string
    endDate: string
}

function JobEntries({ jobId, startDate, endDate }: JobEntriesProps) {
    const theme = useTheme()
    const { entries, isLoading } = useApprovedJobEntries({
        jobId,
        startDate,
        endDate,
        enabled: true,
    })

    if (isLoading) {
        return (
            <View style={styles.loadingContainer}>
                <LoadingIndicator />
            </View>
        )
    }

    if (entries.length === 0) {
        return (
            <View style={styles.emptyEntriesContainer}>
                <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                    No approved entries for this period
                </Text>
            </View>
        )
    }

    return (
        <View style={styles.entriesContainer}>
            <View
                style={[styles.entriesHeader, { borderBottomColor: theme.colors.outlineVariant }]}
            >
                <View style={styles.typeColumn} />
                <Text
                    style={[
                        styles.headerText,
                        styles.userColumn,
                        { color: theme.colors.onSurfaceVariant },
                    ]}
                >
                    User
                </Text>
                <Text
                    style={[
                        styles.headerText,
                        styles.locationColumn,
                        { color: theme.colors.onSurfaceVariant },
                    ]}
                >
                    Location
                </Text>
                <Text
                    style={[
                        styles.headerText,
                        styles.dateColumn,
                        { color: theme.colors.onSurfaceVariant },
                    ]}
                >
                    Date
                </Text>
                <Text
                    style={[
                        styles.headerText,
                        styles.hoursColumn,
                        { color: theme.colors.onSurfaceVariant },
                    ]}
                >
                    Hrs
                </Text>
            </View>
            {entries.map(entry => {
                const userName = entry.expand?.user?.name || 'Unknown'
                const isTravelWithRoute =
                    entry.entry_type === 'travel' && entry.notes?.includes(' -> ')
                const locationName = isTravelWithRoute
                    ? entry.notes?.split('\n')[0]
                    : displayLocationName(entry.expand?.location?.name) || '—'
                const workSeconds = getNetWorkSeconds(entry)

                return (
                    <View
                        key={entry.id}
                        style={[
                            styles.entryRow,
                            { borderBottomColor: theme.colors.surfaceVariant },
                        ]}
                    >
                        <View style={styles.typeColumn}>
                            <Icon
                                source={getEntryTypeIcon(entry.entry_type)}
                                size={14}
                                color={theme.colors.onSurfaceVariant}
                            />
                        </View>
                        <Text
                            style={[
                                styles.cellText,
                                styles.userColumn,
                                { color: theme.colors.onSurface },
                            ]}
                            numberOfLines={1}
                        >
                            {userName}
                        </Text>
                        <Text
                            style={[
                                styles.cellText,
                                styles.locationColumn,
                                { color: theme.colors.onSurfaceVariant },
                            ]}
                            numberOfLines={1}
                        >
                            {locationName}
                        </Text>
                        <Text
                            style={[
                                styles.cellText,
                                styles.dateColumn,
                                { color: theme.colors.onSurface },
                            ]}
                            numberOfLines={1}
                        >
                            {formatDateShort(entry.clock_in)}
                        </Text>
                        <Text
                            style={[
                                styles.hoursText,
                                styles.hoursColumn,
                                { color: theme.colors.primary },
                            ]}
                            numberOfLines={1}
                        >
                            {formatDurationHHMM(workSeconds)}
                        </Text>
                    </View>
                )
            })}
        </View>
    )
}

export default function CustomerDetailPage() {
    const params = useLocalSearchParams<{ orgSlug: string; customerSlug: string }>()
    const theme = useTheme()
    const { customer, isPending } = useCustomer(params.customerSlug)
    const { jobs, isLoading: isJobsLoading } = useCustomerJobs({ customerId: customer?.id })
    const { settings } = useOrgSettings(params.orgSlug)

    const periodConfig = useMemo<PeriodConfig>(
        () => ({
            periodType: settings?.period_type || DEFAULT_PERIOD_TYPE,
            weekStartDay: (settings?.week_start_day ?? DEFAULT_WEEK_START_DAY) as WeekStartDay,
        }),
        [settings?.period_type, settings?.week_start_day]
    )

    const {
        periodDate,
        handlePrevious: handlePreviousPeriod,
        handleNext: handleNextPeriod,
        handleDatePicked,
    } = usePeriodNavigation(periodConfig)

    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

    const { start, end } = getPeriodBounds(periodDate, periodConfig)
    const periodInfo = getPeriodInfo(periodDate, periodConfig)

    const jobIds = useMemo(() => jobs.map(job => job.id), [jobs])
    const { summaryByJob } = useCustomerJobsSummary({
        jobIds,
        startDate: start,
        endDate: end,
        enabled: jobs.length > 0,
    })

    useHeaderOptions({
        buttons: {
            type: 'edit',
            href: `/${params.orgSlug}/settings/customers/${customer?.identifier}/edit`,
        },
        title: customer?.name,
        enabled: !!customer,
    })

    const toggleSection = useCallback((jobId: string) => {
        setExpandedSections(prev => {
            const next = new Set(prev)
            if (next.has(jobId)) {
                next.delete(jobId)
            } else {
                next.add(jobId)
            }
            return next
        })
    }, [])

    const sections: Section[] = useMemo(() => {
        return jobs.map(job => {
            const summary = summaryByJob[job.id] || { totalHours: 0, entryCount: 0 }
            return {
                jobId: job.id,
                jobName: job.name || job.identifier,
                totalHours: summary.totalHours,
                entryCount: summary.entryCount,
                data: expandedSections.has(job.id)
                    ? [{ type: 'entries' as const, jobId: job.id }]
                    : [],
            }
        })
    }, [jobs, expandedSections, summaryByJob])

    const renderHeader = useCallback(() => {
        if (!customer) return null

        const customerWithExpand = customer as CustomerFormData
        const address = customerWithExpand?.expand?.address
        const createdDate = new Date(customer.created).toLocaleDateString()
        const hasAddress = isNonBlankAddress(address)

        const rightContent = (
            <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                Created {createdDate}
            </Text>
        )

        return (
            <View>
                <Card style={styles.collapsibleCard}>
                    <CollapsibleSection
                        title={customer.name}
                        icon="account"
                        isLast
                        rightContent={rightContent}
                    >
                        {hasAddress ? <AddressPanel address={address} showDivider={false} /> : null}
                    </CollapsibleSection>

                    {customer.notes ? (
                        <View style={styles.notesSection}>
                            <Divider />
                            <View style={styles.noteContent}>
                                <Text
                                    variant="bodySmall"
                                    style={{ color: theme.colors.onSurfaceVariant }}
                                >
                                    Notes
                                </Text>
                                <Text variant="bodyMedium">{customer.notes}</Text>
                            </View>
                        </View>
                    ) : null}
                </Card>

                <View style={styles.jobsHeader}>
                    <Icon source="briefcase" size={16} color={theme.colors.onSurfaceVariant} />
                    <Text variant="headlineSmall" style={styles.jobsTitle}>
                        Jobs
                    </Text>
                </View>

                <PeriodNavigator
                    periodLabel={periodInfo.label}
                    onPrevious={handlePreviousPeriod}
                    onNext={handleNextPeriod}
                    periodDate={periodDate}
                    onDatePicked={handleDatePicked}
                />
            </View>
        )
    }, [
        customer,
        theme,
        periodInfo.label,
        handlePreviousPeriod,
        handleNextPeriod,
        handleDatePicked,
        periodDate,
    ])

    const renderSectionHeader = useCallback(
        ({ section }: { section: Section }) => (
            <JobSectionHeader
                section={section}
                expanded={expandedSections.has(section.jobId)}
                isLoading={false}
                onToggle={() => toggleSection(section.jobId)}
            />
        ),
        [expandedSections, toggleSection]
    )

    const renderItem = useCallback(
        ({ item }: { item: { type: 'entries'; jobId: string } }) => (
            <JobEntries jobId={item.jobId} startDate={start} endDate={end} />
        ),
        [start, end]
    )

    const renderEmpty = useCallback(() => {
        if (isJobsLoading) {
            return <LoadingIndicator />
        }
        return (
            <EmptyCard
                icon="briefcase-outline"
                title="No jobs yet"
                subtitle="Jobs associated with this customer will appear here"
            />
        )
    }, [isJobsLoading])

    if (isPending) {
        return (
            <ListWrapper>
                <LoadingIndicator />
            </ListWrapper>
        )
    }

    if (!customer) {
        return (
            <NotFound message="Customer not found. It may have been deleted or you don't have permission to view it." />
        )
    }

    return (
        <ListWrapper>
            <SectionList
                sections={sections}
                keyExtractor={(item, index) => `${item.jobId}-${index}`}
                renderSectionHeader={renderSectionHeader}
                renderItem={renderItem}
                ListHeaderComponent={renderHeader}
                ListEmptyComponent={renderEmpty}
                stickySectionHeadersEnabled={true}
                contentContainerStyle={styles.listContent}
            />
        </ListWrapper>
    )
}

const styles = StyleSheet.create({
    listContent: {
        paddingBottom: 24,
    },
    collapsibleCard: {
        overflow: 'hidden',
    },
    notesSection: {
        paddingBottom: 12,
    },
    noteContent: {
        paddingHorizontal: 16,
        paddingTop: 12,
        gap: 4,
    },
    jobsHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 8,
    },
    jobsTitle: {
        fontWeight: '600',
    },
    loadingContainer: {
        padding: 16,
    },
    emptyEntriesContainer: {
        padding: 16,
        alignItems: 'center',
    },
    entriesContainer: {
        paddingBottom: 8,
    },
    entriesHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
    },
    headerText: {
        fontSize: 11,
        fontWeight: '600',
    },
    entryRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderBottomWidth: StyleSheet.hairlineWidth,
    },
    typeColumn: {
        width: 28,
        justifyContent: 'center',
        alignItems: 'center',
    },
    userColumn: {
        flex: 1,
        paddingRight: 4,
    },
    locationColumn: {
        flex: 1,
        paddingRight: 4,
    },
    dateColumn: {
        width: 50,
        paddingRight: 4,
    },
    hoursColumn: {
        width: 46,
        textAlign: 'right',
    },
    cellText: {
        fontSize: 12,
    },
    hoursText: {
        fontSize: 12,
        fontWeight: '600',
    },
})
